import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import {
  Plus, Trash2, Save, Edit3, X, CalendarDays, Clock, ToggleLeft, ToggleRight, Repeat,
  Ticket, Users, Euro, ScrollText, ExternalLink, Loader2, Copy, Link2
} from 'lucide-react';
import ScopeSelector from '../components/ScopeSelector';

const EMPTY_TIER = { name: '', price: 0, description: '', capacity: 0 };

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', event_date: '', event_time: '',
    is_recurring: false, recurrence_note: '', is_active: true,
    ticketing_enabled: false, ticket_tiers: [], max_tickets_per_customer: null,
  });
  const [scope, setScope] = useState('brand');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await adminApi.get('/admin/events');
      setEvents(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({
      name: '', description: '', event_date: '', event_time: '',
      is_recurring: false, recurrence_note: '', is_active: true,
      ticketing_enabled: false, ticket_tiers: [], max_tickets_per_customer: null,
    });
    setEditId(null);
    setShowForm(false);
  };

  const openEdit = (e) => {
    setForm({
      name: e.name, description: e.description, event_date: e.event_date || '',
      event_time: e.event_time || '', is_recurring: e.is_recurring || false,
      recurrence_note: e.recurrence_note || '', is_active: e.is_active,
      ticketing_enabled: e.ticketing_enabled || false,
      ticket_tiers: e.ticket_tiers || [],
      max_tickets_per_customer: e.max_tickets_per_customer || null,
    });
    setEditId(e.id);
    setScope(e.scope || 'brand');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Inserisci il nome'); return; }
    if (!form.description.trim()) { toast.error('Inserisci la descrizione'); return; }
    if (form.ticketing_enabled && form.ticket_tiers.length === 0) {
      toast.error('Aggiungi almeno una fascia di prezzo'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, scope };
      if (editId) {
        await adminApi.put(`/admin/events/${editId}`, payload);
        toast.success('Evento aggiornato');
      } else {
        await adminApi.post('/admin/events', payload);
        toast.success('Evento creato');
      }
      resetForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo evento?')) return;
    try {
      await adminApi.delete(`/admin/events/${id}`);
      toast.success('Eliminato');
      load();
    } catch { toast.error('Errore'); }
  };

  const handleToggle = async (e) => {
    try {
      await adminApi.put(`/admin/events/${e.id}`, { is_active: !e.is_active });
      load();
      toast.success(e.is_active ? 'Disattivato' : 'Attivato');
    } catch { toast.error('Errore'); }
  };

  const addTier = () => setForm(f => ({ ...f, ticket_tiers: [...f.ticket_tiers, { ...EMPTY_TIER }] }));
  const removeTier = (idx) => setForm(f => ({ ...f, ticket_tiers: f.ticket_tiers.filter((_, i) => i !== idx) }));
  const updateTier = (idx, key, val) => setForm(f => {
    const tiers = [...f.ticket_tiers];
    tiers[idx] = { ...tiers[idx], [key]: val };
    return { ...f, ticket_tiers: tiers };
  });

  const fmtDate = (d) => d ? new Date(d + 'T00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-3xl" data-testid="admin-events-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Eventi</h1>
          <p className="text-[#64748B] text-xs mt-0.5">Crea eventi con biglietteria online integrata</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-event-btn"
          className="flex items-center gap-1.5 px-3 py-2 bg-[#D44E20] text-white text-xs font-bold rounded-xl hover:bg-[#B83D15] transition-colors shadow-sm">
          <Plus size={14} /> Nuovo evento
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mb-5 space-y-4" data-testid="event-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#1A1A1A] text-sm">{editId ? 'Modifica evento' : 'Nuovo evento'}</h3>
            <button onClick={resetForm} className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-[#64748B] hover:bg-gray-200"><X size={14} /></button>
          </div>

          <div>
            <label className="text-[11px] text-[#64748B] font-medium block mb-1">Nome evento <span className="text-[#D44E20]">*</span></label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Es. Serata Jazz dal vivo" maxLength={100} data-testid="event-name-input"
              className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none" />
          </div>

          <div>
            <label className="text-[11px] text-[#64748B] font-medium block mb-1">Descrizione <span className="text-[#D44E20]">*</span></label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Descrizione dell'evento..." rows={3} maxLength={500} data-testid="event-desc-input"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-[#64748B] font-medium block mb-1">Data</label>
              <input type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
                data-testid="event-date-input"
                className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-[11px] text-[#64748B] font-medium block mb-1">Orario</label>
              <input type="time" value={form.event_time} onChange={e => setForm(f => ({ ...f, event_time: e.target.value }))}
                data-testid="event-time-input"
                className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
          </div>

          {/* Recurring */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <button onClick={() => setForm(f => ({ ...f, is_recurring: !f.is_recurring }))} className="flex-shrink-0">
              {form.is_recurring ? <ToggleRight size={22} className="text-[#3ECF8E]" /> : <ToggleLeft size={22} className="text-[#ccc]" />}
            </button>
            <div>
              <p className="text-xs font-medium text-[#1A1A1A]">Evento ricorrente</p>
            </div>
          </div>
          {form.is_recurring && (
            <input type="text" value={form.recurrence_note} onChange={e => setForm(f => ({ ...f, recurrence_note: e.target.value }))}
              placeholder="Es. Ogni venerdi sera" maxLength={100}
              className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none" />
          )}

          {/* Ticketing toggle */}
          <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: form.ticketing_enabled ? '#FEF3C720' : '#f8fafc', border: form.ticketing_enabled ? '1px solid #F59E0B30' : '1px solid transparent' }}>
            <button onClick={() => setForm(f => ({ ...f, ticketing_enabled: !f.ticketing_enabled }))} data-testid="ticketing-toggle" className="flex-shrink-0">
              {form.ticketing_enabled ? <ToggleRight size={22} className="text-[#F59E0B]" /> : <ToggleLeft size={22} className="text-[#ccc]" />}
            </button>
            <div>
              <p className="text-xs font-medium text-[#1A1A1A] flex items-center gap-1.5"><Ticket size={13} /> Biglietteria online</p>
              <p className="text-[10px] text-[#64748B]">Abilita la vendita di biglietti/ingressi dal portale cliente</p>
            </div>
          </div>

          {/* Ticket tiers */}
          {form.ticketing_enabled && (
            <div className="space-y-3 p-4 bg-amber-50/50 border border-amber-200/30 rounded-xl">
              {/* Public checkout URL — only shown for already-saved events (with a slug) */}
              {editId && (() => {
                const editingEvent = events.find(e => e.id === editId);
                if (!editingEvent?.slug) return null;
                const checkoutUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/event/${editingEvent.slug}/checkout`;
                return (
                  <div className="bg-white rounded-xl border border-emerald-200 p-3" data-testid="event-checkout-url-block">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                      <Link2 size={11} /> Link pubblico checkout
                    </p>
                    <div className="flex items-center gap-2 bg-emerald-50/50 border border-emerald-100 rounded-lg px-3 py-2">
                      <span className="font-mono text-[11px] text-slate-700 flex-1 break-all" data-testid="event-checkout-url">{checkoutUrl}</span>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(checkoutUrl); toast.success('Link copiato'); }}
                        data-testid="copy-checkout-url-btn"
                        className="flex-shrink-0 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-2.5 py-1 rounded flex items-center gap-1"
                      >
                        <Copy size={10} /> COPIA
                      </button>
                      <a
                        href={checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="open-checkout-url-btn"
                        className="flex-shrink-0 text-[10px] bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold px-2.5 py-1 rounded flex items-center gap-1"
                      >
                        <ExternalLink size={10} /> APRI
                      </a>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1.5">
                      Linka questo URL dalle tue landing page, social, email o stampa il QR per usarlo offline.
                    </p>
                  </div>
                );
              })()}
              <div className="flex items-center justify-between">
                <label className="text-[11px] text-[#64748B] font-semibold flex items-center gap-1"><Euro size={11} /> Fasce di prezzo</label>
                <button onClick={addTier} data-testid="add-tier-btn" className="text-xs text-[#D44E20] font-semibold hover:underline flex items-center gap-1">
                  <Plus size={12} /> Aggiungi fascia
                </button>
              </div>
              {form.ticket_tiers.map((tier, idx) => (
                <div key={idx} className="bg-white rounded-xl p-3 border border-slate-200 space-y-2" data-testid={`tier-${idx}`}>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <input value={tier.name} onChange={e => updateTier(idx, 'name', e.target.value)}
                        placeholder="Es. Ingresso Base, VIP, Menu Completo" data-testid={`tier-name-${idx}`}
                        className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs focus:border-[#D44E20] focus:outline-none" />
                    </div>
                    <div className="flex items-center gap-1">
                      <input type="number" step="0.50" value={tier.price} onChange={e => updateTier(idx, 'price', parseFloat(e.target.value) || 0)}
                        data-testid={`tier-price-${idx}`}
                        className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-right focus:border-[#D44E20] focus:outline-none" />
                      <span className="text-[10px] text-slate-400">EUR</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <input value={tier.description} onChange={e => updateTier(idx, 'description', e.target.value)}
                        placeholder="Descrizione (opzionale)"
                        className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs focus:border-[#D44E20] focus:outline-none" />
                    </div>
                    <div className="flex items-center gap-1">
                      <input type="number" value={tier.capacity || ''} onChange={e => updateTier(idx, 'capacity', parseInt(e.target.value) || 0)}
                        placeholder="Posti" data-testid={`tier-capacity-${idx}`}
                        className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-right focus:border-[#D44E20] focus:outline-none" />
                      <button onClick={() => removeTier(idx)} className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {form.ticket_tiers.length === 0 && <p className="text-xs text-slate-400 italic">Aggiungi almeno una fascia di prezzo</p>}

              <div>
                <label className="text-[11px] text-[#64748B] font-medium block mb-1">Max biglietti per cliente (facoltativo)</label>
                <input type="number" value={form.max_tickets_per_customer || ''} onChange={e => setForm(f => ({ ...f, max_tickets_per_customer: e.target.value ? parseInt(e.target.value) : null }))}
                  placeholder="Nessun limite" data-testid="max-tickets-input"
                  className="w-32 h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>
          )}

          {/* Per-event T&C override — appears for any event being edited
              (visible whether ticketing is on or off so the admin can
              configure it once). For events not yet created, shows a
              helpful note. Defaults inherit the brand-level master. */}
          {editId ? (
            <EventTermsOverride eventId={editId} />
          ) : (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[11px] text-[#64748B] flex items-start gap-2" data-testid="event-tc-not-yet-available">
              <ScrollText size={14} className="text-[#94A3B8] flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-[#1A1A1A]">Termini & Condizioni dell'evento</strong>: salva prima
                l'evento, poi potrai personalizzare i T&C (rimborsi, clausole speciali) per questo evento.
                Per default si applicano i T&C generali del brand (Branding → Legale).
              </div>
            </div>
          )}

          <ScopeSelector value={scope} onChange={setScope} />

          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} data-testid="save-event-btn"
              className="flex-1 h-10 bg-[#D44E20] text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-[#B83D15] disabled:opacity-50 transition-colors">
              <Save size={14} /> {saving ? 'Salvataggio...' : editId ? 'Aggiorna' : 'Crea evento'}
            </button>
            <button onClick={resetForm} className="h-10 px-5 bg-slate-100 text-[#64748B] text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors">Annulla</button>
          </div>
        </div>
      )}

      {/* Events list */}
      {events.length === 0 && !showForm ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100 shadow-sm">
          <CalendarDays size={32} className="mx-auto mb-3 text-[#ddd]" />
          <p className="text-[#64748B] text-sm font-medium">Nessun evento</p>
          <p className="text-[#aaa] text-xs mt-1">Crea il tuo primo evento con biglietteria integrata</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className={`bg-white rounded-2xl border overflow-hidden shadow-sm transition-all ${ev.is_active ? 'border-slate-100' : 'border-slate-100 opacity-60'}`} data-testid={`event-card-${ev.id}`}>
              <div className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${ev.is_active ? 'bg-[#D44E20]/10' : 'bg-slate-100'}`}>
                  <CalendarDays size={16} className={ev.is_active ? 'text-[#D44E20]' : 'text-[#aaa]'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <h3 className="font-semibold text-[#1A1A1A] text-sm">{ev.name}</h3>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md ${ev.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-[#999]'}`}>
                      {ev.is_active ? 'ATTIVO' : 'INATTIVO'}
                    </span>
                    {ev.ticketing_enabled && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 flex items-center gap-0.5">
                        <Ticket size={8} /> BIGLIETTERIA
                      </span>
                    )}
                    {ev.is_recurring && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 flex items-center gap-0.5">
                        <Repeat size={8} /> RICORRENTE
                      </span>
                    )}
                  </div>
                  <p className="text-[#64748B] text-xs line-clamp-2">{ev.description}</p>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {ev.event_date && (
                      <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                        <CalendarDays size={10} /> {fmtDate(ev.event_date)}
                      </span>
                    )}
                    {ev.event_time && (
                      <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                        <Clock size={10} /> {ev.event_time}
                      </span>
                    )}
                    {ev.ticketing_enabled && ev.total_booked > 0 && (
                      <span className="text-[10px] text-[#D44E20] font-semibold flex items-center gap-1">
                        <Users size={10} /> {ev.total_booked} prenotati
                      </span>
                    )}
                  </div>
                  {/* Ticket tiers summary */}
                  {ev.ticketing_enabled && ev.ticket_tiers?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {ev.ticket_tiers.map((tier, i) => (
                        <span key={i} className="text-[9px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                          {tier.name}: {tier.price > 0 ? `${tier.price.toFixed(2)} EUR` : 'Gratis'}
                          {tier.capacity > 0 && ` (${tier.booked || 0}/${tier.capacity})`}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleToggle(ev)} data-testid={`toggle-event-${ev.id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-slate-100 hover:text-[#64748B] transition-colors">
                    {ev.is_active ? <ToggleRight size={16} className="text-[#3ECF8E]" /> : <ToggleLeft size={16} />}
                  </button>
                  <button onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-slate-100 hover:text-[#64748B] transition-colors">
                    <Edit3 size={13} />
                  </button>
                  <button onClick={() => handleDelete(ev.id)} data-testid={`delete-event-${ev.id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-red-50 hover:text-red-500 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── EventTermsOverride ─────────────────────────────────────────────────
   Per-event T&C panel mounted inside the event editor when editing an
   existing event. Lets the admin override `refund_policy_days` and
   provide a custom Markdown body that replaces the brand-level master
   for this single event. Empty override → inherits the master.
   The "Apri pagina pubblica" link uses the event slug (auto-generated
   server-side at create time) to give a shareable URL like
   `/event/<slug>/termini` that the customer can read before checkout. */
function EventTermsOverride({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [refundDays, setRefundDays] = useState('');
  const [overrideMd, setOverrideMd] = useState('');
  const [expanded, setExpanded] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/events/${eventId}/terms`);
      setData(res.data);
      setRefundDays(
        res.data.refund_policy_days_inherited
          ? ''
          : String(res.data.refund_policy_days ?? '')
      );
      setOverrideMd(res.data.terms_override_md || '');
    } catch {
      // Not blocking — admin may not have configured anything yet
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [eventId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/admin/events/${eventId}/terms`, {
        refund_policy_days: refundDays === '' ? null : parseInt(refundDays, 10),
        terms_override_md: overrideMd.trim() || null,
      });
      toast.success('T&C evento salvati');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[11px] text-[#64748B] flex items-center gap-2" data-testid="event-tc-loading">
        <Loader2 size={13} className="animate-spin" /> Caricamento T&C…
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white border border-amber-200/40 p-4" data-testid="event-terms-override">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="event-terms-toggle">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
          <ScrollText size={14} className="text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-[#1A1A1A]">Termini & Condizioni dell'evento</p>
          <p className="text-[10px] text-[#64748B]">
            {data?.uses_override
              ? `Override attivo · rimborso entro ${data.refund_policy_days}gg`
              : `Eredita master · rimborso entro ${data?.refund_policy_days ?? 7}gg`}
          </p>
        </div>
        <span className="text-[10px] text-[#94A3B8]">{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          {data?.public_url && (
            <a href={data.public_url} target="_blank" rel="noopener noreferrer"
              data-testid="event-terms-public-link"
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-xs text-[#64748B] hover:border-[#D44E20]/30 hover:text-[#D44E20] transition-colors">
              <ExternalLink size={12} /> <span className="font-mono truncate flex-1">{data.public_url}</span>
              <span className="text-[9px] text-[#94A3B8]">apri pagina</span>
            </a>
          )}

          <div>
            <label className="text-[10px] font-medium text-[#64748B] block mb-1">
              Giorni rimborso (vuoto = eredita master)
            </label>
            <input type="number" min={0} max={60} value={refundDays}
              onChange={e => setRefundDays(e.target.value)}
              data-testid="event-tc-refund-days"
              placeholder="Default brand"
              className="w-32 h-9 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs focus:border-[#D44E20] focus:outline-none" />
          </div>

          <div>
            <label className="text-[10px] font-medium text-[#64748B] block mb-1">
              Override T&C (Markdown — vuoto = usa master del brand)
            </label>
            <textarea value={overrideMd} onChange={e => setOverrideMd(e.target.value)}
              data-testid="event-tc-override-md"
              rows={8}
              spellCheck={false}
              placeholder="Lascia vuoto per usare il template T&C generale del brand. Compila solo per clausole specifiche di questo evento."
              className="w-full font-mono text-[11px] leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-2.5 focus:border-[#D44E20] focus:outline-none resize-y" />
          </div>

          <button onClick={handleSave} disabled={saving}
            data-testid="event-tc-save-btn"
            className="w-full h-9 bg-[#D44E20] text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 hover:bg-[#B83D15] disabled:opacity-50">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saving ? 'Salvataggio…' : 'Salva T&C evento'}
          </button>
        </div>
      )}
    </div>
  );
}
